/**
 * Hover labels that also work under a finger.
 *
 * The target, attack, supply and external-track layers explain a symbol with a
 * label that follows the mouse. A touch screen has no hover: MapLibre's
 * mouseenter never fires for a finger, so on a phone or tablet those symbols
 * were dots with no way to ask what they were. Here a tap on the symbol shows
 * the same label and a tap anywhere else on the map dismisses it.
 */

import type { MapLike } from '@/map/MapCanvas';

interface PointerEventLike {
  point: { x: number; y: number };
  originalEvent?: unknown;
}

export function bindHoverLayer<E extends PointerEventLike>(
  map: MapLike,
  layerId: string,
  show: (event: E) => void,
  hide: () => void,
): () => void {
  // The layer click and the map-wide click are the same DOM event; marking it
  // lets the map-wide handler tell "tapped this symbol" from "tapped elsewhere"
  // whichever order MapLibre calls them in.
  let shownBy: unknown = null;
  const keyOf = (event: PointerEventLike) => event.originalEvent ?? event;

  const onEnter = show;
  const onLeave = (): void => hide();
  const onTap = (event: E): void => {
    shownBy = keyOf(event);
    show(event);
  };
  const onMapTap = (event: PointerEventLike): void => {
    if (keyOf(event) !== shownBy) hide();
  };

  map.on('mouseenter', layerId, onEnter);
  map.on('mousemove', layerId, onEnter);
  map.on('mouseleave', layerId, onLeave);
  map.on('click', layerId, onTap);
  map.on('click', onMapTap);

  return () => {
    map.off('mouseenter', layerId, onEnter);
    map.off('mousemove', layerId, onEnter);
    map.off('mouseleave', layerId, onLeave);
    map.off('click', layerId, onTap);
    map.off('click', onMapTap);
  };
}
